import React from "react";

export type HistoricoItem = {
  id?: number | string | null;
  item_id?: number | string | null;
  produto_id?: number | string | null;
  movimentacao_id?: number | string | null;

  tipo?:
    | "entrada"
    | "saida"
    | "perda"
    | "venda"
    | "consumo"
    | "retorno"
    | "ajuste"
    | "standby"
    | "transferir"
    | string
    | null;

  tipo_full?: string | null;
  codigo_movimentacao?: string | null;
  tipo_codigo?: string | null;
  tipo_descricao?: string | null;

  custo_unitario?: string | number | null;
  quantidade?: number | string | null;
  data_movimentacao?: string | null;
  criado_em?: string | null;

  responsavel_id?: number | string | null;
  responsavel_nome?: string | null;

  moeda?: string | null;

  os_numero?: string | null;
  proposta_venda?: string | null;
  motivo?: string | null;
  origem_mov_id?: number | string | null;
};

export type HistoricoPrecosWidgetProps = {
  items: HistoricoItem[];
  title?: string;
  loading?: boolean;
  error?: string | null;
  formatCurrency?: (v: string | number | null | undefined) => string; // (não é usado no custo por moeda, mas mantive p/ compat)
  formatDateTime?: (iso: string | null | undefined) => string;
  onRowClick?: (item: HistoricoItem) => void;
};

// ======================== Default formatters ======================
const defaultFormatCurrency = (v: string | number | null | undefined) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(n);
};

const defaultFormatDateTime = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const data = d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const hora = d.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${data} - ${hora}`;
};

// ============================= Layout =============================
//           0            1      2      3       4        5        6
const COLUMNS = [
  "Responsável",
  "Q.",
  "Tipo",
  "Custo",
  "Moeda", // ⬅️ NOVA COLUNA
  "Variação",
  "Data",
] as const;

// Ajuste do grid: acrescentei uma fração para “Moeda”
const gridTemplate = "2fr 0.8fr 1fr 1.1fr 0.8fr 1fr 1.5fr";

// Paleta pastel clara (mantive a sua escolha original)
const BG_UP = "#FDEAEA"; // aumentou (vermelho pastel)
const BG_DOWN = "#EAF7EA"; // diminuiu (verde pastel)
const BG_FLAT = "#FFF9DB"; // igual (amarelo pastel)
const BG_BASE = "#E7F1FF"; // ponto de partida (azul pastel)

// Acentos (apenas na 1ª célula da linha)
const ACCENT_RED = "#D66";
const ACCENT_GREEN = "#7BBF7B";
const ACCENT_YELLOW = "#E5C100";
const ACCENT_BLUE = "#4A90E2";

const styles: { [k: string]: React.CSSProperties } = {
  wrapper: {
    border: "1px solid #000",
    width: "100%",
    marginTop: 30,
    fontFamily: "Inter, sans-serif",
    boxSizing: "border-box",
  },
  top: {
    height: 35,
    backgroundColor: "#09363f",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 14,
    borderBottom: "1px solid #000",
  },
  legendWrap: {
    padding: "10px 12px",
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "center",
    borderBottom: "1px solid #000",
    background: "#fafafa",
    fontSize: 12,
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#333",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  },
  swatch: {
    width: 14,
    height: 14,
    borderRadius: 3,
    border: "1px solid rgba(0,0,0,0.15)",
    boxSizing: "border-box",
  },
  headerRow: {
    height: 35,
    backgroundColor: "#09363f",
    display: "grid",
    gridTemplateColumns: gridTemplate,
    alignItems: "center",
    color: "#fff",
    fontWeight: 600,
    fontSize: 13,
    boxSizing: "border-box",
    borderBottom: "1px solid #000",
  },
  headerCell: {
    padding: "0 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    boxSizing: "border-box",
    borderRight: "1px solid #000",
  },
  body: {
    backgroundColor: "#fff",
    display: "grid",
    gridTemplateColumns: gridTemplate,
    alignItems: "stretch",
    fontSize: 13,
    boxSizing: "border-box",
  },
  bodyCell: {
    padding: "10px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: "#333",
    boxSizing: "border-box",
    borderRight: "1px solid #000",
  },
  error: {
    padding: 12,
    color: "#c0362c",
    fontSize: 13,
    borderTop: "1px solid #000",
  },
};

// ============================= Utils ==============================
const parsePrice = (v: string | number | null | undefined): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

function capitalizeTipo(tipo?: string | null) {
  if (!tipo) return "—";
  return tipo.charAt(0).toUpperCase() + tipo.slice(1);
}

function getRowBackgroundByTrend(
  trend?: "up" | "down" | "flat" | "na"
): string | undefined {
  if (trend === "up") return BG_UP;
  if (trend === "down") return BG_DOWN;
  if (trend === "flat") return BG_FLAT;
  if (trend === "na") return BG_BASE; // ponto de partida
  return undefined;
}

function getLeftBorderByTrend(
  trend?: "up" | "down" | "flat" | "na"
): string | undefined {
  if (trend === "up") return `4px solid ${ACCENT_RED}`;
  if (trend === "down") return `4px solid ${ACCENT_GREEN}`;
  if (trend === "flat") return `4px solid ${ACCENT_YELLOW}`;
  if (trend === "na") return `4px solid ${ACCENT_BLUE}`;
  return undefined;
}

// Formata custo conforme a moeda da linha
const formatByCurrency = (
  value: number | null | undefined,
  iso?: string | null
) => {
  if (value == null || !Number.isFinite(value)) return "—";
  const c = (iso || "BRL").toUpperCase();
  // locale só para símbolo/format: USD → en-US, demais → pt-BR (pode ajustar se quiser)
  const locale = c === "USD" ? "en-US" : "pt-BR";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: c,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // fallback caso venha moeda não suportada pelo Intl
    return `${value.toFixed(2)} (${c})`;
  }
};

// =========================== Component ============================
const HistoricoPrecosWidget: React.FC<HistoricoPrecosWidgetProps> = ({
  items,
  title = "Histórico de Preços do Produto",
  loading = false,
  error = null,
  formatCurrency = defaultFormatCurrency, // mantido para compat, mas custo usa formatByCurrency
  formatDateTime = defaultFormatDateTime,
  onRowClick,
}) => {
  const showEmpty = !loading && (!items || items.length === 0);

  // 1) Ordena por data (antigas -> novas) para calcular variação
  // 2) Variação só quando a moeda é a MESMA da linha anterior
  const chronological = React.useMemo(() => {
    const arr = (items ?? [])
      .map((it) => ({
        ...it,
        _price: parsePrice(it.custo_unitario),
        _moeda: String(it.moeda || "BRL").toUpperCase(),
      }))
      .sort((a, b) => {
        const ta = a.data_movimentacao ? Date.parse(a.data_movimentacao) : 0;
        const tb = b.data_movimentacao ? Date.parse(b.data_movimentacao) : 0;
        return ta - tb;
      }) as (HistoricoItem & {
      _price: number | null;
      _moeda: string;
      _trend?: "up" | "down" | "flat" | "na";
      _pct?: number | null;
    })[];

    let prevPrice: number | null = null;
    let prevCurrency: string | null = null;

    for (const r of arr) {
      const p = r._price;
      const curr = r._moeda;

      // se mudou de moeda em relação à referência anterior → variação N/A e reinicia base
      if (prevCurrency !== null && curr !== prevCurrency) {
        r._trend = "na";
        r._pct = null;
        prevPrice = p;
        prevCurrency = curr;
        continue;
      }

      if (prevPrice === null || prevPrice === 0 || p === null) {
        r._trend = "na";
        r._pct = null;
      } else {
        const pct = ((p - prevPrice) / prevPrice) * 100;
        if (Math.abs(pct) < 1e-10) {
          r._trend = "flat";
          r._pct = 0;
        } else if (pct > 0) {
          r._trend = "up";
          r._pct = pct;
        } else {
          r._trend = "down";
          r._pct = pct;
        }
      }

      if (p !== null) {
        prevPrice = p;
        prevCurrency = curr;
      }
    }

    return arr;
  }, [items]);

  // Exibir (novas -> antigas)
  const rows = React.useMemo(() => {
    return [...chronological].sort((a, b) => {
      const ta = a.data_movimentacao ? Date.parse(a.data_movimentacao) : 0;
      const tb = b.data_movimentacao ? Date.parse(b.data_movimentacao) : 0;
      return tb - ta;
    });
  }, [chronological]);

  const renderDelta = (pct: number | null | undefined, trend?: string) => {
    if (trend === "na") return "N/A"; // moeda mudou ou primeiro da série
    if (pct == null) return "—";
    const arrow = pct > 0 ? "↑" : pct < 0 ? "↓" : "→";
    const sign = pct > 0 ? "+" : "";
    return `${arrow} ${sign}${pct.toFixed(2)}%`;
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.top}>{title}</div>

      {/* Legenda / Explicação */}
      <div style={styles.legendWrap} aria-label="Legenda de variações de preço">
        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: BG_UP }} />
          <span>
            <strong>↑ Aumentou</strong> (vermelho pastel)
          </span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: BG_DOWN }} />
          <span>
            <strong>↓ Diminuiu</strong> (verde pastel)
          </span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: BG_FLAT }} />
          <span>
            <strong>→ Igual</strong> (amarelo pastel)
          </span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: BG_BASE }} />
          <span>
            <strong>Ponto de partida</strong> (variação N/A ou mudança de moeda)
          </span>
        </div>
      </div>

      {/* Cabeçalho */}
      <div style={styles.headerRow}>
        {COLUMNS.map((label, idx) => (
          <div
            key={label}
            style={{
              ...styles.headerCell,
              borderRight:
                idx === COLUMNS.length - 1 ? "none" : "1px solid #000",
            }}
          >
            {label}
          </div>
        ))}
      </div>

      {/* Corpo */}
      <div style={styles.body}>
        {showEmpty
          ? Array.from({ length: COLUMNS.length }).map((_, idx) => (
              <div
                key={`empty-${idx}`}
                style={{
                  ...styles.bodyCell,
                  borderRight:
                    idx === COLUMNS.length - 1 ? "none" : "1px solid #000",
                }}
              >
                —
              </div>
            ))
          : rows.map((c, rowIdx) => {
              const bg = getRowBackgroundByTrend(c._trend);
              const leftBorder = getLeftBorderByTrend(c._trend);

              const custoFmt = formatByCurrency(c._price, c._moeda);
              const moedaFmt = c._moeda || "—";

              const cells: (string | number)[] = [
                c.responsavel_nome || (c.responsavel_id ?? "—"),
                c.quantidade ?? "—",
                capitalizeTipo(c.tipo),
                custoFmt, // custo formatado por moeda
                moedaFmt, // coluna "Moeda"
                renderDelta(c._pct ?? null, c._trend),
                (c.data_movimentacao &&
                  (typeof defaultFormatDateTime === "function"
                    ? defaultFormatDateTime(c.data_movimentacao)
                    : c.data_movimentacao)) ||
                  "—",
              ];

              return cells.map((val, colIdx) => (
                <div
                  key={`${c.id}-${colIdx}`}
                  onClick={onRowClick ? () => onRowClick!(c) : undefined}
                  style={{
                    ...styles.bodyCell,
                    background: bg,
                    borderLeft:
                      colIdx === 0 && leftBorder ? leftBorder : "none",
                    borderRight:
                      colIdx === COLUMNS.length - 1 ? "none" : "1px solid #000",
                    borderTop: rowIdx === 0 ? "none" : "1px solid #000",
                    cursor: onRowClick ? "pointer" : "default",
                  }}
                  title={
                    c._trend === "na"
                      ? "Ponto de partida (sem base anterior) ou mudança de moeda"
                      : `Variação: ${c._pct?.toFixed(2)}%`
                  }
                >
                  {val}
                </div>
              ));
            })}
      </div>

      {error && (
        <div style={styles.error}>Erro ao carregar histórico: {error}</div>
      )}
    </div>
  );
};

export default HistoricoPrecosWidget;
